import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  ShoppingCart, 
  Ticket, 
  Trophy, 
  TrendingUp,
  Clock,
  AlertCircle,
  Loader2,
  Activity,
  Calendar,
  Users
} from 'lucide-react';
import { fetchDashboardData } from '../actions/dashboardActions';
import styles from '../styles/Dashboard.module.css';

const numberFormatter = new Intl.NumberFormat('en-IN');

/**
 * Formats a value for display
 * @param {*} value - Value to format
 * @returns {string} Formatted value or '--'
 */
const formatValue = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '--';
  }
  return numberFormatter.format(Number(value));
};

/**
 * Dashboard Component - Enterprise-grade dashboard with metrics and information cards
 */
const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { data, loading, error } = useSelector((state) => state.dashboard);
  const [currentTime, setCurrentTime] = useState('');

  const dashboardData = data || {};
  const metrics = dashboardData.metrics || {};
  const activityLog = dashboardData.activityLog || {};
  const upcomingDraws = dashboardData.upcomingDraws || [];
  const playerStatus = dashboardData.playerStatus || {};
  const timezone = dashboardData?.period?.timezone || 'Asia/Kolkata';

  useEffect(() => {
    dispatch(fetchDashboardData());
  }, [dispatch]);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      try {
        const formatted = new Intl.DateTimeFormat('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZone: timezone,
        }).format(now);
        setCurrentTime(formatted);
      } catch (err) {
        setCurrentTime(now.toLocaleTimeString());
      }
    };

    updateTime();
    const intervalId = setInterval(updateTime, 1000);
    return () => clearInterval(intervalId);
  }, [timezone]);

  const metricsConfig = useMemo(
    () => [
      {
        label: 'Sale Points',
        value: formatValue(metrics.salePoints),
        icon: ShoppingCart,
        to: `/${slug || 'root'}/reports/all-summary`,
      },
      {
        label: 'Pending Tickets',
        value: formatValue(metrics.pendingTickets),
        icon: Ticket,
      },
      {
        label: 'Win Points',
        value: formatValue(metrics.winPoints),
        icon: Trophy,
      },
      {
        label: 'Profit',
        value: formatValue(metrics.profit),
        icon: TrendingUp,
      },
    ],
    [metrics, slug]
  );

  const formatDrawTime = (draw) => {
    if (!draw) return '--';
    const sourceDate = draw.scheduledAt || draw.draw_time;
    if (sourceDate) {
      try {
        return new Intl.DateTimeFormat('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: timezone,
        }).format(new Date(sourceDate));
      } catch (err) {
        // ignore formatting error and fall back to existing string
      }
    }
    if (draw.scheduledAtFormatted) {
      const parts = draw.scheduledAtFormatted.split(' ');
      return parts[parts.length - 1] || draw.scheduledAtFormatted;
    }
    return '--';
  };

  return (
    <div className={styles.dashboard}>
      <div className={styles.dashboardHeader}>
        <h1 className={styles.dashboardTitle}>DASHBOARD</h1>
        {currentTime && (
          <div className={styles.timeDisplay}>
            <Clock size={16} />
            <span>{currentTime}</span>
          </div>
        )}
      </div>

      {error && (
        <div className={styles.errorAlert}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {loading && !data ? (
        <div className={styles.loadingContainer}>
          <Loader2 className={styles.spinner} size={48} />
        </div>
      ) : (
        <>
          <div className={styles.metricsGrid}>
            {metricsConfig.map((metric) => (
              <MetricCard
                key={metric.label}
                {...metric}
                onNavigate={metric.to ? () => navigate(metric.to) : undefined}
              />
            ))}
          </div>

          <div className={styles.topCardsContainer}>
            <InfoCard title="Activity Log" icon={Activity} headerColor="navy">
              <div className={styles.activityList}>
                <ActivityRow label="Today" value={formatValue(activityLog.today)} />
                <ActivityRow label="Yesterday" value={formatValue(activityLog.yesterday)} />
                <ActivityRow label="Current Week" value={formatValue(activityLog.currentWeek)} />
              </div>
            </InfoCard>

            <InfoCard title="Upcoming Draws" icon={Calendar} headerColor="black">
              <div className={styles.drawsList}>
                {upcomingDraws.length ? (
                  upcomingDraws.map((draw) => (
                    <DrawRow
                      key={`${draw.draw_id}-${draw.draw_time}`}
                      gameName={draw.game_name || draw.game_code || 'N/A'}
                      drawId={draw.draw_id}
                      time={formatDrawTime(draw)}
                    />
                  ))
                ) : (
                  <div className={styles.emptyState}>No upcoming draws.</div>
                )}
              </div>
            </InfoCard>
          </div>

          <div className={styles.playerStatusContainer}>
            <InfoCard title="Player Status" icon={Users} headerColor="navy" fullWidth>
              <div className={styles.playerStatus}>
                <div className={styles.totalPlayers}>
                  <span className={styles.totalLabel}>Total Players</span>
                  <span className={styles.totalValue}>
                    {formatValue(playerStatus.totalPlayers)}
                  </span>
                </div>

                <div className={styles.statusList}>
                  <StatusRow label="Logged" value={formatValue(playerStatus.logged)} />
                  <StatusRow label="Active" value={formatValue(playerStatus.active)} />
                  <StatusRow label="Idle" value={formatValue(playerStatus.idle)} />
                </div>

                {playerStatus.breakdown && (
                  <div className={styles.breakdown}>
                    <div className={styles.breakdownDivider}></div>
                    <div className={styles.breakdownTitle}>Breakdown</div>
                    <div className={styles.statusList}>
                      <StatusRow
                        label="Waiting for Approval"
                        value={formatValue(playerStatus.breakdown.waitingForApproval)}
                      />
                      <StatusRow
                        label="No Login Attempt"
                        value={formatValue(playerStatus.breakdown.noLoginAttempt)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </InfoCard>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * MetricCard Component - Displays a metric with icon
 */
const MetricCard = ({ label, value, icon: Icon, onNavigate }) => {
  const isClickable = typeof onNavigate === 'function';

  return (
    <div
      className={`${styles.metricCard} ${isClickable ? styles.metricCardClickable : ''}`}
      onClick={isClickable ? onNavigate : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigate();
        }
      } : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `${label}: ${value}. Open Show All with Summary report.` : undefined}
    >
      <div className={styles.metricContent}>
        <div className={styles.metricInfo}>
          <span className={styles.metricLabel}>{label}</span>
          <span className={styles.metricValue}>{value}</span>
        </div>
        <div className={styles.metricIcon}>
          <Icon size={32} />
        </div>
      </div>
    </div>
  );
};

/**
 * InfoCard Component - Container for information sections
 */
const InfoCard = ({ title, icon: Icon, headerColor = 'black', fullWidth = false, children }) => (
  <div className={`${styles.infoCard} ${fullWidth ? styles.infoCardFullWidth : ''}`}>
    <h2 className={`${styles.infoCardTitle} ${styles[`infoCardTitle${headerColor.charAt(0).toUpperCase() + headerColor.slice(1)}`]}`}>
      <span>{title}</span>
      {Icon && <Icon size={20} className={styles.infoCardIcon} />}
    </h2>
    <div className={styles.infoCardContent}>{children}</div>
  </div>
);

/**
 * ActivityRow Component - Displays activity log row
 */
const ActivityRow = ({ label, value }) => (
  <div className={styles.activityRow}>
    <span className={styles.activityLabel}>{label}</span>
    <span className={styles.activityValue}>{value}</span>
  </div>
);

/**
 * DrawRow Component - Displays upcoming draw information
 */
const DrawRow = ({ gameName, drawId, time }) => (
  <div className={styles.drawRow}>
    <div className={styles.drawInfo}>
      <span className={styles.drawGameName}>
        {gameName} <span className={styles.drawSeparator}>-</span> <span className={styles.drawId}>{drawId}</span>
      </span>
    </div>
    <span className={styles.drawTime}>{time}</span>
  </div>
);

/**
 * StatusRow Component - Displays player status row
 */
const StatusRow = ({ label, value }) => (
  <div className={styles.statusRow}>
    <span className={styles.statusLabel}>{label}</span>
    <span className={styles.statusValue}>{value}</span>
  </div>
);

export default Dashboard;
